import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import api from "@/lib/api";

const GROUP_FOR_SLUG = {
  "metabolic-research-peptides": "Metabolic Research Peptides",
  "research-peptides": "Research Peptides",
  "lab-supplies": "Lab Supplies",
};
const GROUP_ORDER = ["Metabolic Research Peptides", "Research Peptides", "Lab Supplies"];

// Friendly short labels (override product_name where needed)
const LABEL_OVERRIDES = {
  "NA-TRZ": "TRZ",
  "NA-RT": "RT",
  "NA-GHKCU": "GHK-Cu",
  "NA-NAD": "NAD+",
  "NA-MOTSC": "MOTS-c",
  "NA-CJCNODAC": "CJC No DAC",
  "NA-KPV": "KPV",
  "NA-BACWATER": "BAC Water",
};

export default function ShopProductsMenu({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/menu").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, []);

  const groups = rows.reduce((acc, r) => {
    const g = GROUP_FOR_SLUG[r.category_slug] || r.category || "Other";
    (acc[g] = acc[g] || []).push(r);
    return acc;
  }, {});
  const orderedGroups = GROUP_ORDER.filter((g) => groups[g]?.length).concat(
    Object.keys(groups).filter((g) => !GROUP_ORDER.includes(g))
  );

  const close = () => { setOpen(false); onNavigate?.(); };

  return (
    <div className="relative" ref={ref}>
      <div className="inline-flex items-center gap-1">
        <Link
          to="/shop"
          onClick={close}
          className="link-line py-1"
          data-testid="nav-shop-products"
        >
          Shop Products
        </Link>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="p-1 -ml-0.5 rounded hover:bg-[var(--paper-3)] transition-colors"
          aria-label="Toggle Shop menu"
          aria-expanded={open}
          aria-haspopup="true"
          data-testid="nav-shop-products-toggle"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.8} />
        </button>
      </div>

      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[920px] rounded-xl border border-[var(--line)] bg-[#0B0B1F] shadow-[0_20px_60px_rgba(0,0,0,0.85)] overflow-hidden z-50 ring-1 ring-[var(--terra)]/20"
          data-testid="shop-products-menu"
        >
          <div className="grid grid-cols-3 divide-x divide-[var(--line)] bg-[#0B0B1F]">
            {orderedGroups.map((group) => (
              <div key={group} className="p-5">
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--terra)] mb-3">{group}</div>
                <ul className="space-y-1">
                  {groups[group].map((p) => {
                    const label = LABEL_OVERRIDES[p.sku] || p.product_name;
                    const stockTone = !p.in_stock
                      ? { dot: "bg-[#A30414]", text: "text-[#A30414]", txt: "Out" }
                      : p.stock_qty <= 5
                      ? { dot: "bg-[#F4B400]", text: "text-[#F4B400]", txt: `${p.stock_qty} left` }
                      : { dot: "bg-[var(--terra)]", text: "text-[var(--ink-soft)]", txt: `${p.stock_qty} in stock` };
                    return (
                      <li key={`${p.sku}-${p.variant}`}>
                        <Link
                          to={`/shop/${p.sku}?variant=${encodeURIComponent(p.variant)}`}
                          onClick={close}
                          className={`group flex items-center justify-between gap-3 px-2 py-2 rounded-md transition-colors ${p.in_stock ? "hover:bg-[var(--terra)]/10" : "opacity-60 hover:bg-[#A30414]/10"}`}
                          data-testid={`shop-products-item-${p.sku}-${p.variant.replace(/\s+/g, "")}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`inline-block h-1.5 w-1.5 rounded-full ${stockTone.dot} shrink-0`} />
                            <span className="text-[13.5px] text-[var(--ink)] group-hover:text-[var(--terra)] truncate">{label}</span>
                            <span className="font-mono text-[11px] text-[var(--ink-muted)] shrink-0">{p.variant}</span>
                          </div>
                          <div className="flex flex-col items-end shrink-0">
                            <span className="font-mono text-[12px] text-[var(--ink)] group-hover:text-[var(--terra)]">${p.price.toFixed(p.price % 1 === 0 ? 0 : 2)}</span>
                            <span className={`font-mono text-[9.5px] tracking-[0.06em] uppercase ${stockTone.text}`}>{stockTone.txt}</span>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-[var(--line)] px-5 py-3 flex items-center justify-between bg-[#08081A]">
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink-muted)]">{rows.length} SKUs · Koveralabs-verified</span>
            <Link to="/shop" onClick={close} className="text-[12px] font-medium text-[var(--terra)] hover:text-[var(--terra-2)]" data-testid="shop-products-view-all">
              View full catalog →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// Keep export for any mobile-nav consumers — they get a stub of SKUs derived from /menu lazily.
// Layout.jsx uses this for the mobile menu; downgrade to a graceful fallback for backwards compat.
export const PRODUCT_ENTRIES = [];
